import { createFileRoute } from "@tanstack/react-router";
import GameDayPage, { buildGameDaySchemas, SITE, type Faq } from "@/components/GameDayPage";
import heroImage from "@/assets/georgia-florida-halloween-sprinter-limo-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";
import limoImage from "@/assets/limo-style-sprinter-party-bus-lounge-bar.jpg?format=webp&quality=72&w=1000&imagetools";
import execImage from "@/assets/sprinter-exec-green.jpg?format=webp&quality=72&w=1000&imagetools";
import jetImage from "@/assets/jet-style-sprinter-white-interior-captain-seats.jpg?format=webp&quality=72&w=1000&imagetools";
import suvImage from "@/assets/suburban-suv-game-day-atlanta.webp?format=webp&quality=72&w=1000&imagetools";

import suvCaptainImage from "@/assets/luxury-suburban-suv-captain-seats-atlanta.jpg?format=webp&quality=72&w=1000&imagetools";

const PAGE_URL = `${SITE}/georgia-vs-florida-game-transportation`;
const TITLE = "Georgia vs Florida Party Bus & Limo in Atlanta | Oct 31";
const DESC =
  "Georgia–Florida is at Mercedes-Benz Stadium in Atlanta on Oct 31, the first time outside Jacksonville since 1995. Luxury Sprinter limo: bar, 2 TVs, premium sound. Book before it's gone.";

const faqs: Faq[] = [
  { q: "When should we book a party bus for Georgia–Florida in Atlanta?", a: "As soon as possible. Our custom fleet is small, and this is one of the biggest dates of the year in Atlanta." },
  { q: "Is a Sprinter limo the same as a party bus?", a: "It's the party bus experience, with a bar, TVs, lights and sound, in a newer, cleaner and more comfortable luxury Sprinter with a professional chauffeur." },
  { q: "Can you take us to a Halloween party after the game?", a: "Yes. Add an after-party ride to your booking and your chauffeur takes the whole group on to your Halloween night plans." },
  { q: "How many people fit?", a: "The Limo Style Sprinter seats up to 14, the Executive Sprinter up to 13, the Jet Style Sprinter up to 7, the captain-seat Suburban up to 6 and the bench-seat Suburban up to 7." },
  { q: "Where's the drop-off at Mercedes-Benz Stadium?", a: "As close to your gate as game day closures allow. Your chauffeur confirms the pickup spot before the game ends." },
  { q: "Can we bring alcohol?", a: "Yes, it is BYOB. Guests 21+ may bring their own drinks, and the bar is set with glassware and ice. We do not provide or sell alcohol." },
];

export const Route = createFileRoute("/georgia-vs-florida-game-transportation")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { name: "twitter:card", content: "summary_large_image" },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: buildGameDaySchemas({
      url: PAGE_URL,
      name: "Georgia vs. Florida Party Bus & Sprinter Limo in Atlanta",
      description: DESC,
      serviceType: "Game day party bus and Sprinter limo transportation",
      areas: ["Atlanta"],
      faqs,
      crumb: "Georgia vs. Florida Transportation",
    }),
  }),
  component: Page,
});

function Page() {
  return (
    <GameDayPage
      eyebrow="Oct 31 · Mercedes-Benz Stadium"
      h1="Georgia vs. Florida 2026 in Atlanta: Party Bus & Sprinter Limo"
      intro="This is a game you want to enjoy together. Text me your pickup and group size, and I'll help plan your Georgia vs. Florida trip in a custom Sprinter or private Suburban."
      heroImg={heroImage}
      heroAlt="Sprinter limo party bus with LED lighting for Georgia vs. Florida in Atlanta on Halloween"
      champagneText="BYOB: guests 21+ may bring their own drinks. The bar is set with glassware and ice. We do not provide or sell alcohol."
      smallPrint="BYOB for guests 21+. We do not provide or sell alcohol. Features vary by vehicle."
      bigDates={{ heading: "Oct 31 Availability", events: ["Georgia vs. Florida"] }}
      steps={{
        title: "Your Oct 31 Plan",
        items: [
          { t: "Pickup at home or hotel", d: "Anywhere in metro Atlanta. Costumes welcome." },
          { t: "Pre-game with the bar and TVs", d: "Watch the pre-game shows with your playlist ready for the ride in." },
          { t: "Drop-off near the stadium", d: "As close to your gate as closures allow." },
          { t: "Chauffeur waiting", d: "Your Sprinter is staged nearby for the whole game." },
          { t: "Optional Halloween after-party ride", d: "Keep the Sprinter and head straight to your Halloween night plans." },
        ],
      }}
      vehicles={{
        title: "Pick Your Ride and Pricing",
        intro: "A Georgia Florida game party bus experience, in a Sprinter limo built for comfort. Book the vehicle that fits your group.",
        list: [
          { name: "Limo Style Sprinter", cap: "Up to 14 guests", detail: "The party bus experience, with bar, 2 TVs, LED ceiling and premium sound.", img: limoImage, alt: "Limo style Sprinter lounge interior with built-in bar" },
          { name: "Executive Sprinter", cap: "Up to 13 guests", detail: "Premium leather and a more relaxed ride.", img: execImage, alt: "Executive Sprinter limo interior for Georgia vs. Florida in Atlanta" },
          { name: "Jet Style Sprinter", cap: "Up to 7 guests", detail: "White leather captain seats and first-class comfort for smaller groups.", img: jetImage, alt: "Jet style Sprinter with white leather captain seats for Georgia vs. Florida" },
          { name: "Luxury Suburban SUV, Captain Seats", cap: "Up to 6 guests", detail: "Leather captain's chairs for a smaller, private ride.", img: suvCaptainImage, alt: "Luxury Suburban SUV with leather captain seats for game day in Atlanta" },
          { name: "Suburban SUV, Bench Seats", cap: "Up to 7 guests", detail: "Bench seating for a group of up to 7.", img: suvImage, alt: "Black Suburban SUV with bench seating for game day in Atlanta" },
        ],
        note: "The bar, TVs, sound system, LED lighting, stand-up headroom and CarPlay are on every Sprinter. Our Suburbans are a quieter, private ride for smaller groups.",
        showPricing: true,
      }}
      faqTitle="Georgia vs. Florida Party Bus FAQ"
      faqs={faqs}
      occasion="Game Day"
      bookTitle="Reserve Your Oct 31 Sprinter"
    />
  );
}
